import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import Logo from "./brand/Logo";
import Button from "./ui/Button";
import Card from "./ui/Card";
import MobileNav from "./layout/MobileNav";
import { cn } from "../utils/classNames";

const navItems = [
  { to: "/", label: "Home", icon: "⌂" },
  { to: "/quiz", label: "Take the Quiz", shortLabel: "Quiz", icon: "✧" },
  { to: "/recommendations", label: "Recs", icon: "▷" },
  { to: "/mood-board", label: "Mood Board", shortLabel: "Mood", icon: "☾" },
  { to: "/trending", label: "Trending", icon: "♨" },
  { to: "/discover", label: "Discover", icon: "⌕" },
  { to: "/guest-mode", label: "Guest Mode", icon: "♕" },
];

const desktopNavLinkClass = ({ isActive }) =>
  cn(
    "inline-flex min-h-11 items-center gap-2 border-b-2 px-1 text-sm font-semibold transition",
    isActive
      ? "border-hookd-empress text-hookd-empress"
      : "border-transparent text-hookd-cream hover:border-hookd-gold/60 hover:text-hookd-gold"
  );

const mobileNavLinkClass =
  "flex min-h-12 w-full items-center justify-between rounded-hookd border border-white/10 bg-white/5 px-4 py-3 text-base font-semibold text-hookd-cream shadow-sm hover:border-hookd-empress/60 hover:text-hookd-empress";

function useMobileBottomNav() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(query.matches);

    update();
    query.addEventListener?.("change", update);
    query.addListener?.(update);

    return () => {
      query.removeEventListener?.("change", update);
      query.removeListener?.(update);
    };
  }, []);

  return isMobile;
}

export default function Header({ user = null, isAdmin = false, onLogout }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showMobileBottomNav = useMobileBottomNav();

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleLogout = async () => {
    await onLogout?.();
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-hookd-line bg-hookd-ink/95 text-hookd-cream shadow-panel backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link to="/" onClick={closeMobileMenu} className="shrink-0" aria-label="Hook'd Curatress home">
          <Logo size="nav" />
        </Link>

        <button
          type="button"
          aria-controls="mobile-nav-menu"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((open) => !open)}
          className="min-h-11 rounded-hookd border border-hookd-line bg-white/5 px-4 py-2 text-sm font-semibold text-hookd-cream sm:hidden"
        >
          {mobileMenuOpen ? "Close" : "Menu"}
        </button>

        <nav className="hidden flex-wrap items-center justify-end gap-5 md:flex">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={desktopNavLinkClass}>
              <span aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/admin" className={desktopNavLinkClass}>
              <span aria-hidden="true">♛</span>
              <span>Admin</span>
            </NavLink>
          )}
          {user ? (
            <>
              <NavLink to="/profile" className={desktopNavLinkClass}>Profile</NavLink>
              <NavLink to="/saved-shows" className={desktopNavLinkClass}>Saved</NavLink>
              <Button type="button" onClick={handleLogout} variant="ghost" size="sm">
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button as={Link} to="/login" variant="outline" size="sm">Login</Button>
              <Button as={Link} to="/signup" variant="primary" size="sm">Sign Up</Button>
            </>
          )}
        </nav>
      </div>

      {mobileMenuOpen && (
        <Card
          as="nav"
          id="mobile-nav-menu"
          data-testid="mobile-nav-menu"
          className="mx-4 mb-4 grid gap-2 border-hookd-line p-3 sm:hidden"
        >
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} onClick={closeMobileMenu} className={mobileNavLinkClass}>
              <span>{item.label}</span>
              <span aria-hidden="true">{item.icon}</span>
            </Link>
          ))}
          {isAdmin && (
            <Link to="/admin" onClick={closeMobileMenu} className={mobileNavLinkClass}>Admin</Link>
          )}
          {user ? (
            <>
              <Link to="/profile" onClick={closeMobileMenu} className={mobileNavLinkClass}>Profile</Link>
              <Link to="/saved-shows" onClick={closeMobileMenu} className={mobileNavLinkClass}>Saved</Link>
              <button type="button" onClick={handleLogout} className={mobileNavLinkClass}>
                <span>Logout</span>
                <span aria-hidden="true">↗</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={closeMobileMenu} className={mobileNavLinkClass}>Login</Link>
              <Link to="/signup" onClick={closeMobileMenu} className={mobileNavLinkClass}>Sign Up</Link>
            </>
          )}
        </Card>
      )}
      {showMobileBottomNav && <MobileNav />}
    </header>
  );
}
